import React, { useCallback, useEffect, useRef, useState } from "react";

import { navId } from "../../layout/header";

type Point = [number, number];
type Points = Point[];

const getOpposedLine = (pointA: Point, pointB: Point) => {
  const lengthX = pointB[0] - pointA[0];
  const lengthY = pointB[1] - pointA[1];
  return {
    length: Math.sqrt(Math.pow(lengthX, 2) + Math.pow(lengthY, 2)),
    angle: Math.atan2(lengthY, lengthX),
  };
};

const controlPoint = (current: Point, previous: Point, next: Point, reverse?: boolean) => {
  const prevPoint = previous || current;
  const nextPoint = next || current;
  const smoothing = 0.2;
  const opposedLine = getOpposedLine(prevPoint, nextPoint);

  // If is end-control-point, add PI to the angle to go backward
  const angle = opposedLine.angle + (reverse ? Math.PI : 0);
  const length = opposedLine.length * smoothing;

  // The control point position is relative to the current point
  const x = current[0] + Math.cos(angle) * length;
  const y = current[1] + Math.sin(angle) * length;
  return [x, y];
};

const getWavePath = (points: Points) =>
  points.reduce((final, point, index) => {
    if (index === 0) {
      return `m ${point[0]} ${point[1]}`;
    }

    // Generate the control points to make the curves between each point
    const [controlStartX, controlStartY] = controlPoint(points[index - 1], points[index - 2], point);
    const [controlEndX, controlEndY] = controlPoint(point, points[index - 1], points[index + 1], true);

    return `${final} C ${controlStartX},${controlStartY} ${controlEndX},${controlEndY} ${point[0]},${point[1]}`;
  }, "");

const getSectionsData = (sections: NodeListOf<Element>) => {
  const totalDimensions = { height: 0, width: 0 };
  const points: [number, number][] = [];
  const clientWidth = document.documentElement.clientWidth;
  const isMobile = clientWidth < 768;
  let lastSectionEnd = 0;

  // Get total height sum of all sections
  for (let i = 0; i < sections.length; i++) {
    const sectionElem = sections[i];
    const rect = sectionElem.getBoundingClientRect();

    if (!totalDimensions.width) {
      totalDimensions.width = rect.width;
      const firstPoint: Point = [rect.width / 2, 0];
      points.push(firstPoint);
    }

    totalDimensions.height += rect.height;

    const xCenter = totalDimensions.width / 2;
    const pathStart = i === 0 ? 0 : lastSectionEnd;
    const leftCurve = i % 2 === 0;
    const offset = Math.min(125, xCenter / 7);
    let xPos = leftCurve ? xCenter - offset : xCenter + offset;

    if (isMobile) {
      const gutter = clientWidth * 0.05;
      xPos = leftCurve ? gutter : clientWidth - gutter;
    }

    const point: Point = [xPos, pathStart + rect.height / 2];

    lastSectionEnd = pathStart + rect.height;
    points.push(point);

    if (i === sections.length - 1) {
      points.push([xCenter, pathStart + rect.height]);
    }
  }

  return { points, totalDimensions };
};

const TimelineWave = () => {
  const [pathLength, setPathLength] = useState(0);
  const unseenPathRef = useRef<SVGPathElement>(null);
  const seenPathRef = useRef<SVGPathElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const circleRef = useRef<SVGCircleElement>(null);
  const gradientRef = useRef<SVGLinearGradientElement>(null);
  const widthRef = useRef(0);

  const walkTimeline = useCallback(() => {
    if (!seenPathRef.current || !containerRef.current || !circleRef.current || !gradientRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const navRect = document.getElementById(navId)?.getBoundingClientRect();
    const heightWithNavOffset = document.documentElement.clientHeight + (navRect?.height || 0);
    const containerStart = heightWithNavOffset / 2 - containerRect.top;

    const scrollPercentage = containerStart / containerRect.height;
    const drawLength = pathLength * scrollPercentage;

    if (drawLength > 0) {
      // Draw in reverse
      seenPathRef.current.setAttribute("stroke-dashoffset", (pathLength - drawLength).toString());
    }

    // Move the dot and gradient to the correct position
    const progressPoint = seenPathRef.current.getPointAtLength(scrollPercentage * pathLength);
    circleRef.current.setAttribute("transform", `translate(${progressPoint.x},${progressPoint.y})`);
    gradientRef.current.setAttribute("y1", `${progressPoint.y - heightWithNavOffset / 2.5}`);
    gradientRef.current.setAttribute("y2", `${progressPoint.y}`);
  }, [pathLength]);

  const generateTimeline = useCallback(() => {
    if (widthRef.current === window.innerWidth) {
      return;
    }

    if (!unseenPathRef.current || !seenPathRef.current || !svgRef.current) {
      return;
    }

    widthRef.current = window.innerWidth;
    const timelineSections = document.querySelectorAll(".timeline-section-wrapper");
    const { totalDimensions, points } = getSectionsData(timelineSections);

    // Set the dimensions on the SVG
    svgRef.current.setAttribute("width", totalDimensions.width.toString());
    svgRef.current.setAttribute("height", totalDimensions.height.toString());
    svgRef.current.setAttribute("viewBox", `0 0 ${totalDimensions.width} ${totalDimensions.height}`);

    // Draw the line
    const linePath = getWavePath(points);
    unseenPathRef.current.setAttribute("d", linePath);
    seenPathRef.current.setAttribute("d", linePath);

    // Add the offset to prep for "drawing" on scroll
    const pathLength = unseenPathRef.current.getTotalLength();
    setPathLength(pathLength);

    seenPathRef.current.setAttribute("stroke-dasharray", `${pathLength} ${pathLength}`);
    seenPathRef.current.setAttribute("stroke-dashoffset", pathLength.toString());

    walkTimeline();
  }, [walkTimeline]);

  useEffect(() => {
    generateTimeline();
  }, [generateTimeline]);

  useEffect(() => {
    window.addEventListener("scroll", walkTimeline);

    return () => {
      window.removeEventListener("scroll", walkTimeline);
    };
  }, [walkTimeline]);

  useEffect(() => {
    window.addEventListener("resize", generateTimeline);

    return () => {
      window.removeEventListener("resize", generateTimeline);
    };
  }, [generateTimeline]);

  return (
    <div className="h-full w-full absolute inset-0 z-0" ref={containerRef}>
      <svg fill="none" xmlns="http://www.w3.org/2000/svg" ref={svgRef}>
        <path fillRule="evenodd" clipRule="evenodd" stroke="white" strokeWidth={4} ref={unseenPathRef} />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          stroke="url(#timelineGradient)"
          strokeLinecap="round"
          strokeWidth={11}
          ref={seenPathRef}
        />

        <g filter="url(#dropshadow)">
          <circle cx="0" cy="0" r="9" fill="#42A4FF" stroke="white" strokeWidth="4" ref={circleRef} />
        </g>
        <defs>
          <filter id="dropshadow" x="-3" y="-3" width="9" height="9">
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="0" />
            <feGaussianBlur stdDeviation="7" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.1 0 0 0 0 0.2 0 0 0 0 1 0 0 0 0.3 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_118" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_118" result="shape" />
          </filter>
          <linearGradient
            id="timelineGradient"
            x1="0"
            y1="0"
            x2="0"
            y2="0"
            gradientUnits="userSpaceOnUse"
            ref={gradientRef}
          >
            <stop offset="0%" stopColor="white" />
            <stop offset="100%" stopColor="#42A4FF" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
};

export default TimelineWave;
